import * as Sentry from '@sentry/browser';

/**
 * Extracts error message from error. If the API returns either error or message key, returns error
 * message from that. Otherwise, devise returns an array of errors so we join them together and
 * return.
 * @param {Error} error
 * @return {String}
 */
export const handleError = (error) => {
  const data = error?.response?.data;
  if (!data) return 'A network error occurred. Please try again.';

  // API returns errors in either 'error' or 'message' key
  if (data.error || data.message) {
    return data.error || data.message;
  }

  // Devise auth returns an array of errors and errorMessage joins them into a single string
  if (Array.isArray(data.errors)) {
    return data.errors.join(' ');
  }

  return 'An unexpected error occurred, please try again.';
};
  
/**
 * Function to log unexpected errors related to authentication reqeusts to sentry.
 */
export const handleUnexpectedAuthError = (error, dispatch, type) => {
  // Something unexpectedly went wrong, capture error in Sentry
  captureNetworkException(error, { source: 'auth' });
  dispatch({
    type,
    payload: {
      error: 'Something went wrong. Try refreshing the page or contact support',
    },
  });
};

const capturedErrors = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

const trackCapturedError = (error) => {
  if (!capturedErrors || !error || typeof error !== 'object') return false;
  if (capturedErrors.has(error)) return true;
  capturedErrors.add(error);
  return false;
};

export const sanitizeEndpointForSentry = (endpoint) => {
  if (!endpoint || typeof endpoint !== 'string') return endpoint;

  const maskSegment = (segment) => {
    if (!segment) return segment;
    if (/^\d+$/.test(segment)) return '[id]';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return '[id]';
    if (segment.includes('.') && segment.length >= 20) return '[token]'; // JWT-like
    if (segment.length >= 8 && /[A-Za-z]/.test(segment) && /\d/.test(segment)) return '[token]';
    if (
      segment.length >= 16 &&
      /^[A-Za-z0-9_-]+$/.test(segment) &&
      (/\d/.test(segment) || /[_-]/.test(segment))
    ) return '[token]';
    if (/^[0-9a-f]{16,}$/i.test(segment)) return '[token]';
    return segment;
  };

  const sanitizePath = (pathname) => {
    try {
      if (!pathname || typeof pathname !== 'string') return pathname;
      return pathname
        .split('/')
        .map(maskSegment)
        .join('/');
    } catch (e) {
      return pathname;
    }
  };

  try {
    if (/^https?:\/\//i.test(endpoint)) {
      const url = new URL(endpoint);
      return `${url.origin}${sanitizePath(url.pathname)}`;
    }
  } catch (e) {
    // fall through
  }

  if (endpoint.startsWith('/')) return sanitizePath(endpoint);
  return endpoint;
};

const sanitizeStringForSentry = (value) => {
  if (!value || typeof value !== 'string') return value;

  // Replace absolute URLs first.
  value = value.replace(/https?:\/\/[^\s)]+/gi, (match) => sanitizeEndpointForSentry(match));

  // Replace relative paths with query strings (e.g. /api/v1/...?...)
  value = value.replace(/\/[\w\-./]+\?[^\s)]+/g, (match) => sanitizeEndpointForSentry(match));

  return value;
};

const getHeaderCaseInsensitive = (headers, headerName) => {
  if (!headers || !headerName) return undefined;
  const target = headerName.toLowerCase();
  const key = Object.keys(headers).find((k) => k.toLowerCase() === target);
  return key ? headers[key] : undefined;
};

/**
 * Capture exceptions with extra context, especially helpful for generic browser NetworkErrors.
 * Intended for both axios errors and fetch() failures.
 * @param {Error} error
 * @param {Object} context
 * @param {boolean} [context.captureAbort=false] Set true to capture aborted/canceled requests.
 */
export const captureNetworkException = (error, context = {}) => {
  const isOffline = globalThis.navigator?.onLine === false;
  const isAbort =
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.__CANCEL__ === true || // axios CancelToken uses __CANCEL__
    error?.code === 'ERR_CANCELED' ||
    /aborted|abort|canceled|cancelled/i.test(error?.message || '');

  if (isAbort && !context.captureAbort) return;
  if (trackCapturedError(error)) return;

  const axiosConfig = error?.config;
  const axiosResponse = error?.response;
  const sanitizedMessage = sanitizeStringForSentry(error?.message) || 'NetworkError';

  const extra = {
    source: context.source,
    endpoint: sanitizeEndpointForSentry(context.endpoint || axiosConfig?.url),
    method: context.method || axiosConfig?.method,
    status: context.status || axiosResponse?.status,
    statusText: context.statusText || axiosResponse?.statusText,
    isOffline,
    isAbort,
    axiosRequestId: getHeaderCaseInsensitive(axiosResponse?.headers, 'x-request-id'),
    originalErrorName: error?.name,
    originalMessage: sanitizedMessage,
    originalStack: sanitizeStringForSentry(error?.stack),
    ...context.extra,
  };

  const tags = {
    error_kind: 'network',
    network_offline: isOffline ? 'true' : 'false',
    network_abort: isAbort ? 'true' : 'false',
    ...context.tags,
  };

  const safeError = new Error(sanitizedMessage);
  safeError.name = error?.name || 'Error';

  Sentry.captureException(safeError, { extra, tags });
};
